import React, { useState, useEffect } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import ReusableTable from "./../components/ReusableTable";
import { getApiUrl } from "./../utils/api";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const DiscountPage = () => {
  const [discounts, setDiscounts] = useState([]);
  const [editId, setEditId] = useState(null);
  const [showModal, setShowModal] = useState(false);

  // ================= FETCH =================
  const fetchDiscounts = async () => {
    try {
      const res = await fetch(getApiUrl("discount-list"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });

      const data = await res.json();
      if (data.success) setDiscounts(data.data);
    } catch {
      toast.error("Failed to fetch discounts");
    }
  };

  useEffect(() => {
    fetchDiscounts();
  }, []);

  // ================= DELETE =================
  const handleDelete = async (id) => {
    const res = await fetch(getApiUrl("discount-delete"), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ discountId: id }),
    });

    const result = await res.json();
    if (result.success) {
      toast.success("Deleted successfully");
      fetchDiscounts();
    }
  };

  // ================= MODAL =================
  const openAddModal = () => {
    setEditId(null);
    formik.resetForm();
    setShowModal(true);
  };

  const openEditModal = (row) => {
    setEditId(row._id);
    formik.setValues({
      discountCode: row.discountCode,
      discountType: row.discountType,
      discountValue: row.discountValue,
      expiryDate: row.expiryDate.split("T")[0],
      maxUsers: row.maxUsers || "",
      productItem: row.productItem || "",
    });
    setShowModal(true);
  };

  // ================= FORM =================
  const formik = useFormik({
    initialValues: {
      discountCode: "",
      discountType: "flat",
      discountValue: "",
      expiryDate: "",
      maxUsers: "",
      productItem: "",
    },

    validationSchema: Yup.object({
      discountCode: Yup.string().required("Discount code required"),
      discountType: Yup.string().required("Discount type required"),
      discountValue: Yup.number().required("Discount value required"),
      expiryDate: Yup.string().required("Expiry date required"),
      productItem: Yup.number().required("Product item required"),
    }),

    onSubmit: async (values) => {
      const payload = { ...values };
      if (editId) payload.discountId = editId;

      const url = editId
        ? getApiUrl("discount-update")
        : getApiUrl("discount-add");

      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const result = await res.json();

      if (result.success) {
        toast.success(editId ? "Updated successfully" : "Added successfully");
        setShowModal(false);
        fetchDiscounts();
      } else {
        toast.error(result.message);
      }
    },
  });

  // ================= TABLE =================
  const columns = [
    { name: "Code", selector: (row) => row.discountCode },
    { name: "Type", selector: (row) => row.discountType },
    { name: "Value", selector: (row) => row.discountValue },
    {
      name: "Expiry",
      selector: (row) => new Date(row.expiryDate).toLocaleDateString(),
    },
    {
      name: "Limit",
      selector: (row) => row.maxUsers || "Unlimited",
    },
    { name: "Product", selector: (row) => row.productItem },
    {
      name: "Actions",
      cell: (row) => (
        <>
          <button
            className="btn btn-warning btn-sm me-2"
            onClick={() => openEditModal(row)}
          >
            <i className="fa-regular fa-pen-to-square"></i>
          </button>
          <button
            className="btn btn-danger btn-sm"
            onClick={() => handleDelete(row._id)}
          >
            <i className="fa-regular fa-trash-can"></i>
          </button>
        </>
      ),
    },
  ];

  // ================= UI =================
  return (
    <div className="container mt-4">
      <ToastContainer position="top-right" />

      <div className="d-flex justify-content-between mb-3">
        <h3 className="fw-bold">Discount Management</h3>
        <button className="btn btn-primary" onClick={openAddModal}>
          + Add Discount
        </button>
      </div>

      <ReusableTable columns={columns} data={discounts} title="Discount List" />

      {showModal && (
        <div
          className="modal fade show"
          style={{ display: "block", background: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content rounded-4">
              <div className="modal-header">
                <h4 className="modal-title fw-bold">
                  {editId ? "Edit Discount" : "Add Discount"}
                </h4>
                <button
                  className="btn-close"
                  onClick={() => setShowModal(false)}
                ></button>
              </div>

              <form onSubmit={formik.handleSubmit}>
                <div className="modal-body">
                  <div className="floating-label-group mb-3">
                    <input
                      type="text"
                      name="discountCode"
                      placeholder="Discount Code"
                      value={formik.values.discountCode}
                      onChange={formik.handleChange}
                    />
                    <label>Discount Code</label>
                  </div>

                  <div className="mb-3">
                    <label className="fw-bold">Discount Type</label>
                    <select
                      name="discountType"
                      className="form-select"
                      value={formik.values.discountType}
                      onChange={formik.handleChange}
                    >
                      <option value="flat">Flat</option>
                      <option value="percentage">Percentage</option>
                    </select>
                  </div>

                  <div className="floating-label-group mb-3">
                    <input
                      type="number"
                      name="discountValue"
                      placeholder="Discount Value"
                      value={formik.values.discountValue}
                      onChange={formik.handleChange}
                    />
                    <label>Discount Value</label>
                  </div>

                  <div className="floating-label-group mb-3">
                    <input
                      type="date"
                      name="expiryDate"
                      value={formik.values.expiryDate}
                      onChange={formik.handleChange}
                    />
                    <label>Expiry Date</label>
                  </div>
                  <div className="floating-label-group mb-3">
                    <input
                      type="number"
                      name="productItem"
                      placeholder="Product Item Number"
                      value={formik.values.productItem}
                      onChange={formik.handleChange}
                    />
                    <label>Product Item</label>
                  </div>

                  <div className="floating-label-group">
                    <input
                      type="number"
                      name="maxUsers"
                      placeholder="Max Users (optional)"
                      value={formik.values.maxUsers}
                      onChange={formik.handleChange}
                    />
                    <label>Max Users</label>
                  </div>
                </div>

                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowModal(false)}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-success">
                    {editId ? "Update Discount" : "Add Discount"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DiscountPage;
